import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useMedicalStaffOptionsQuery } from '../hooks/use-medical-staff';
import { useTranslateOption } from '../hooks/use-translate-option';
import {
  MEDICAL_STAFF_PER_PAGE_OPTIONS,
  useMedicalStaffListStore,
} from '../store/medical-staff-list.store';

// PRD doesn't spec a debounce window; 300 ms matches mock latency and avoids
// re-fetching on every keystroke once the real backend is wired.
const SEARCH_DEBOUNCE_MS = 300;

export function MedicalStaffListFilters() {
  const { t } = useTranslation('admin');
  const filters = useMedicalStaffListStore((s) => s.filters);
  const perPage = useMedicalStaffListStore((s) => s.perPage);
  const setFilters = useMedicalStaffListStore((s) => s.setFilters);
  const setPerPage = useMedicalStaffListStore((s) => s.setPerPage);

  // Departments come from the feature-local option pool — same source the invite
  // panel uses, so adds via "+ Add" (Slice 5) immediately show here.
  const { data: options } = useMedicalStaffOptionsQuery();
  const departmentOptions = options?.departments ?? [];
  const { translateDepartment } = useTranslateOption();

  // Search-only debounce: local state drives the input for instant typing
  // feedback; the store (and therefore the list query) only updates after the
  // trailing debounce. Other selects stay immediate — they are discrete clicks.
  const [searchLocal, setSearchLocal] = useState(filters.search);
  const debouncedSearch = useDebouncedValue(searchLocal, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setSearchLocal(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters({ search: debouncedSearch });
    }
  }, [debouncedSearch]);

  return (
    <div className="filter-bar">
      <div className="filter-bar__search">
        <input
          type="text"
          className="filter-bar__search-input"
          placeholder={t('medicalStaffManagement.filters.searchPlaceholder')}
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
        />
        <Search className="filter-bar__search-icon" size={16} />
      </div>

      <select
        className="filter-bar__select"
        value={filters.department}
        onChange={(e) => setFilters({ department: e.target.value })}
      >
        <option value="">{t('medicalStaffManagement.filters.allDepartments')}</option>
        {departmentOptions.map((d) => (
          <option key={d} value={d}>
            {translateDepartment(d)}
          </option>
        ))}
      </select>

      <select
        className="filter-bar__select"
        value={perPage}
        onChange={(e) => setPerPage(Number(e.target.value))}
      >
        {MEDICAL_STAFF_PER_PAGE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {t('medicalStaffManagement.filters.itemsPerPage', { count: n })}
          </option>
        ))}
      </select>
    </div>
  );
}
